import React, { FC, useState } from 'react'
import { useSelector } from 'react-redux'
import { Avatar, Row, Col, Typography, Button, ColProps, message, Modal as AntModal } from 'antd'
import { useTranslation } from 'react-i18next'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Modal, ModalProps } from '~/Components/Modal'
import { UserData } from '~/Services/UserService.d'
import { getDefaultOrganization } from '~/Store/User/selectors'
import { Utils } from '~/Utils'
import { LicenseCards } from '~/Components/LicenseCards/LicenseCards'
import { UserService as AdminUserService } from '~/Services/AdminUsers'

const { Title } = Typography

interface RemoveUserProps extends Omit<ModalProps, 'onOk'> {
  user: Partial<UserData>
  licenses: any[]
  onOk?: (data: Partial<UserData>) => void
  onRemoveSuccess?: () => void
}

export const RemoveUser: FC<RemoveUserProps> = props => {
  const { t } = useTranslation()
  const {
    onOk,
    onCancel,
    loadingData,
    confirmLoading,
    user,
    licenses,
    onRemoveSuccess,
    ...rest
  } = props
  const defaultOrg = useSelector(getDefaultOrganization)
  const [removing, setRemoving] = useState(false)

  const colProps: ColProps = {
    xs: 24,
    md: 12,
  }

  const handleRemoveClick = () => {
    const fullname = Utils.combineStrings([user.firstname, user.lastname])
    
    AntModal.confirm({
      title: t('confirmRemove'),
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <p>
            {t('confirmRemoveMessage')}{' '}
            <strong style={{ color: '#ff4d4f' }}>
              {fullname || user.email}
            </strong>{' '}
            {t('fromOrganization')}?
          </p>
          <p style={{ color: '#faad14', marginTop: '16px' }}>
            {t('removeWarning')}
          </p>
        </div>
      ),
      okText: t('remove'),
      cancelText: t('cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        if (!defaultOrg?.guid) {
          message.error(t('organizationNotFound'))
          return
        }

        try {
          setRemoving(true)
          await AdminUserService.Delete.removeUserFromOrganization(
            defaultOrg.guid,
            user.id as string
          )
          message.success(t('removeUserSuccess'))
          
          if (onRemoveSuccess) {
            onRemoveSuccess()
          }
          
          if (onCancel) {
            onCancel()
          }
        } catch (error: any) {
          console.error('Error removing user:', error)
          message.error(
            error?.response?.data?.message || t('removeUserFailed')
          )
        } finally {
          setRemoving(false)
        }
      },
    })
  }

  const handleOk = () => {
    if (onOk) onOk({ ...user })
  }

  const fullname = Utils.combineStrings([user.firstname, user.lastname])

  return (
    <Modal
      visible
      title={t('userInfo')}
      maskClosable={false}
      keyboard={false}
      loadingData={loadingData}
      onCancel={onCancel}
      onOk={handleOk}
      width={650}
      {...rest}
    >
      {user && defaultOrg && (
        <>
          <Row gutter={[16, 8]}>
            <Col xs={24} md={6}>
              <Avatar
                shape="square"
                size={140}
                src={user.avatarUrl}
                style={{
                  backgroundColor: user.avatarUrl
                    ? undefined
                    : Utils.stringToColour(user.id),
                }}
                gap={1}
              >
                {(fullname || user.email)?.charAt(0).toLocaleUpperCase()}
              </Avatar>
            </Col>
            <Col xs={24} md={18}>
              <Row gutter={8}>
                <Col span={24}>
                  <Row align="stretch">
                    <Title style={{ flex: 1, paddingTop: '4px' }} level={5}>
                      {fullname}
                      {defaultOrg.companyName && (
                        <> &bull;{` ${defaultOrg.companyName}`}</>
                      )}
                    </Title>
                    <Button
                      danger
                      loading={removing || confirmLoading}
                      type="link"
                      onClick={handleRemoveClick}
                    >
                      {t('removeUser')}
                    </Button>
                  </Row>
                </Col>
                <Col span={24}>{user.email}</Col>
                <Col span={24}>{t('productOwner')}</Col>
                <Col span={24}>{user.phoneNumber}</Col>
                <Col span={24}>{user.invitationStatus}</Col>
              </Row>
            </Col>
          </Row>
          <Row gutter={[8, 8]} style={{ marginTop: '25px' }}>
            <Col span={24}>
              <Title level={5}>{t('personalAddress')}</Title>
            </Col>
            <Col span={24}>{user.profile?.address}</Col>
            <Col span={24}>{user.profile?.address2}</Col>
          </Row>
          <Row gutter={[8, 8]} style={{ marginTop: '25px' }}>
            <Col span={24}>
              <Title level={5}>{t('companyAddress')}</Title>
            </Col>
            <Col span={24}>
              {Utils.combineStrings(
                [
                  defaultOrg.billingAddress,
                  defaultOrg.billingAddress2,
                  defaultOrg.billingCity,
                  defaultOrg.billingState,
                  defaultOrg.billingZip,
                ],
                ', ',
              )}
            </Col>
            <Col span={24} />
          </Row>
          {licenses.length > 0 && (
            <Row gutter={[8, 8]} style={{ marginTop: '25px' }}>
              <Col span={24}>
                <Title level={5}>{t('licenses')}</Title>
              </Col>
              {licenses.map(l => (
                <LicenseCards key={l.guid} license={l} col={colProps} />
              ))}
            </Row>
          )}
        </>
      )}
    </Modal>
  )
}